import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, CircularProgress, Popper, Stack, TextField, Typography} from 'views/components';
import {InputAdornment} from '@mui/material';

import backend from 'utils/backend';

import {Translate, translate} from '../Translate';

const SkillsTextbox = (props) => {
  const {isExecutingSkill, onExecuteSkill} = props;

  const [value, setValue] = React.useState('');
  const [skills, setSkills] = React.useState([]);
  const [isLoadingSkills, setIsLoadingSkills] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);

  const anchorRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const command = React.useMemo(() => {
    if (!value.startsWith('/')) {
      return {
        isSlashCommand: false,
        commandName: '',
        isComplete: false,
        promptText: '',
      };
    }

    const matchedSkill = [...skills]
        .filter((skill) => {
          const skillCommand = `/${skill.name}`;
          return value === skillCommand || value.startsWith(`${skillCommand} `);
        })
        .sort((left, right) => (right?.name || '').length - (left?.name || '').length)[0] || null;

    if (matchedSkill?.name) {
      const matchedSkillPrefix = `/${matchedSkill.name}`;

      return {
        isSlashCommand: true,
        commandName: matchedSkill.name,
        isComplete: value.length > matchedSkillPrefix.length,
        promptText: value.length > matchedSkillPrefix.length ? value.slice(matchedSkillPrefix.length + 1) : '',
      };
    }

    const firstSpaceIndex = value.indexOf(' ');

    return {
      isSlashCommand: true,
      commandName: value.slice(1, firstSpaceIndex === -1 ? value.length : firstSpaceIndex),
      isComplete: firstSpaceIndex !== -1,
      promptText: firstSpaceIndex === -1 ? '' : value.slice(firstSpaceIndex + 1),
    };
  }, [skills, value]);

  const filteredSkills = command.isSlashCommand && !command.isComplete ?
    skills.filter((skill) => {
      if (!command.commandName) {
        return true;
      }

      const escapedCommandName = command.commandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`^${escapedCommandName}`, 'i').test(skill?.name || '');
    }) :
    [];

  const selectedSkill = skills.find(
      (skill) => (skill?.name || '').toLowerCase() === command.commandName.toLowerCase(),
  ) || null;
  const showMenu = isMenuOpen && command.isSlashCommand && !command.isComplete;
  const menuWidth = anchorRef.current ? `${anchorRef.current.offsetWidth}px` : '100%';

  const focusInput = () => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleChange = (nextValue) => {
    setValue(nextValue);
  };

  const selectSkill = (skill) => {
    if (!skill?.name) {
      return;
    }

    setIsMenuOpen(false);
    setValue(`/${skill.name} `);
    focusInput();
  };

  const executeSkill = async () => {
    if (isExecutingSkill || !selectedSkill || !command.isComplete) {
      return;
    }

    const response = await onExecuteSkill(selectedSkill, command.promptText);

    if (response !== null) {
      setValue('');
      setIsMenuOpen(false);
    }
  };

  const handleKeyDown = (event) => {
    if (showMenu) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMenuOpen(false);
        return;
      }

      if (isLoadingSkills || filteredSkills.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredSkills.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + filteredSkills.length) % filteredSkills.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const selectedOption = filteredSkills[highlightedIndex] || filteredSkills[0];

        if (selectedOption) {
          event.preventDefault();
          selectSkill(selectedOption);
        }
        return;
      }
    }

    if (event.key === 'Enter' && command.isSlashCommand) {
      event.preventDefault();
      executeSkill();
    }
  };

  React.useEffect(() => {
    let isMounted = true;

    const loadSkills = async () => {
      setIsLoadingSkills(true);

      try {
        const response = await backend.skills.getSkills();

        if (!isMounted) {
          return;
        }

        setSkills(
            [...response.list]
                .sort((left, right) => (left?.name || '').localeCompare(right?.name || '')),
        );
      } catch {
        if (!isMounted) {
          return;
        }

        setSkills([]);
      } finally {
        if (isMounted) {
          setIsLoadingSkills(false);
        }
      }
    };

    loadSkills();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!command.isSlashCommand || command.isComplete) {
      setIsMenuOpen(false);
      return;
    }

    setIsMenuOpen(true);
    setHighlightedIndex(0);
  }, [command.commandName, command.isComplete, command.isSlashCommand]);

  return (
    <Box
      sx = {{
        p: '12px',
      }}
    >
      <Box ref={anchorRef}>
        <TextField
          value={value}
          onChange={handleChange}
          fullWidth
          placeholder={translate('skills.SkillsModule.Textbox.Placeholder')}
          autoComplete="off"
          disabled={isExecutingSkill}
          inputRef={inputRef}
          InputProps={{
            endAdornment: isExecutingSkill ? (
              <InputAdornment position='end'>
                <CircularProgress color='inherit' size={20} />
              </InputAdornment>
            ) : null,
          }}
          onFocus={() => {
            if (command.isSlashCommand && !command.isComplete) {
              setIsMenuOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiOutlinedInput-root': {
              'backgroundColor': 'common.white',
              'borderRadius': '4px',
              '& fieldset': {
                'borderStyle': 'dashed',
                'borderColor': '#A8A49E',
              },
            },
            '& .MuiOutlinedInput-input': {
              'px': '16px',
              'py': '10px',
              'fontSize': '11px',
              'lineHeight': '150%',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              'color': '#A8A49E',
              'opacity': 1,
            },
          }}
        />
      </Box>
      <Popper
        open={showMenu}
        anchorEl={anchorRef.current}
        onClickAway={() => setIsMenuOpen(false)}
        placement='bottom-start'
        zIndex={1400}
        GrowSx={{
          width: menuWidth,
        }}
      >
        <Stack
          sx={{
            width: menuWidth,
            maxHeight: '280px',
            overflowY: 'auto',
            py: '6px',
          }}
        >
          {
            isLoadingSkills &&
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='center'
              sx={{
                px: '16px',
                py: '18px',
                gap: '10px',
              }}
            >
              <CircularProgress size={18} />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#6B6760',
                  lineHeight: '150%',
                }}
              >
                <Translate expression='skills.SkillsModule.Textbox.Loading' />
              </Typography>
            </Stack>
          }
          {
            !isLoadingSkills && filteredSkills.length === 0 &&
            <Typography
              sx={{
                px: '16px',
                py: '12px',
                fontSize: '12px',
                fontWeight: 400,
                color: '#A8A49E',
                lineHeight: '150%',
              }}
            >
              <Translate expression='skills.SkillsModule.Textbox.NoMatch' />
            </Typography>
          }
          {
            !isLoadingSkills && filteredSkills.map((skill, index) => {
              const isHighlighted = index === highlightedIndex;

              return (
                <ButtonBase
                  key={skill.skillRn}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectSkill(skill)}
                  sx={{
                    'width': '100%',
                    'justifyContent': 'flex-start',
                    'textAlign': 'left',
                    'px': '14px',
                    'py': '10px',
                    'backgroundColor': isHighlighted ? '#F8F6F3' : '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#F8F6F3',
                    },
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{
                      width: '100%',
                      gap: '16px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#1A1916',
                        lineHeight: '100%',
                      }}
                    >
                      /{skill.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                        color: '#A8A49E',
                        lineHeight: '100%',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {skill.groupName}
                    </Typography>
                  </Stack>
                </ButtonBase>
              );
            })
          }
        </Stack>
      </Popper>
    </Box>
  );
};

SkillsTextbox.propTypes = {
  isExecutingSkill: PropTypes.bool.isRequired,
  onExecuteSkill: PropTypes.func.isRequired,
};

export default React.memo(SkillsTextbox);
